import { useCallback, useRef, useSyncExternalStore, useMemo } from 'react';

export const useDisposable = <T extends Disposable>(callback: () => T, deps: any[]): T => {
    // Use a ref to hold the instance and state
    const instanceRef = useRef<{
        instance: T | null;
        depsSnapshot: any[];
    }>({
        instance: null,
        depsSnapshot: [],
    });

    // Memoize deps to detect changes
    /* eslint-disable-next-line react-hooks/exhaustive-deps -- intentional */
    const depsSnapshot = useMemo(() => deps, deps);

    // Create a subscribe function that handles disposal on unmount
    const subscribe = useCallback((_onStoreChange: () => void) => {
        // Return unsubscribe function that runs on unmount
        return () => {
            if (instanceRef.current.instance) {
                instanceRef.current.instance[Symbol.dispose]();
                instanceRef.current.instance = null;
            }
        };
    }, []);

    // Get the current instance or create a new one if needed
    const getSnapshot = useCallback(() => {
        const ref = instanceRef.current;

        // Check if deps changed
        const depsChanged = ref.depsSnapshot !== depsSnapshot;

        // If we have no instance or deps changed, create a new one
        if (!ref.instance || depsChanged) {
            // If we had a previous instance due to deps change, dispose it
            if (ref.instance && depsChanged) {
                ref.instance[Symbol.dispose]();
            }

            // Create a new instance
            ref.instance = callback();
            ref.depsSnapshot = depsSnapshot;
        }

        return ref.instance;
    }, [callback, depsSnapshot]);

    // Use the sync external store hook to manage the instance lifecycle
    const instance = useSyncExternalStore(subscribe, getSnapshot);

    return instance;
};
